import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { formatApiError } from "../../lib/api";
import { acceptAgentInvite, getAgentTerms } from "../../lib/supportWorkspace";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [agree, setAgree] = useState(false);
  const [terms, setTerms] = useState(null);
  const [state, setState] = useState({ status: "form" });

  useEffect(() => {
    getAgentTerms().then(setTerms).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) return;
    setState({ status: "loading" });
    try {
      await acceptAgentInvite({ token, name, password, agree_support_terms: true });
      setState({ status: "success" });
    } catch (err) {
      setState({ status: "error", message: formatApiError(err) });
    }
  };

  return (
    <main className="max-w-md mx-auto px-4 py-16" data-testid="support-accept-invite-page">
      <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm text-center">
        {state.status === "success" ? (
          <>
            <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-700" aria-hidden="true" />
            <h1 className="mt-4 font-display text-2xl font-bold">You're in</h1>
            <p className="mt-2 text-sm text-slate-600">Your support-agent account is active.</p>
            <Link to="/login" className="mt-6 inline-flex px-5 py-2.5 rounded-lg btn-brand text-sm font-medium">
              Sign in
            </Link>
          </>
        ) : (
          <>
            <h1 className="font-display text-2xl font-bold mb-2">Accept your support-agent invite</h1>
            <p className="text-sm text-slate-600 mb-6">Set a name and password to activate your account.</p>
            {!token && <p className="text-sm text-rose-600 mb-4" role="alert">This link is missing an invite token.</p>}
            <form onSubmit={submit} className="space-y-3 text-left">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" required
                     data-testid="support-invite-name-input" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              <input value={password} onChange={(e) => setPassword(e.target.value)} type="password"
                     placeholder="Choose a password" required minLength={8}
                     data-testid="support-invite-password-input" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />

              {terms && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 max-h-40 overflow-y-auto"
                     data-testid="support-agent-terms-summary">
                  {terms.summary}
                </div>
              )}
              <label className="flex items-start gap-2 text-xs text-slate-700">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)}
                       data-testid="support-agent-terms-checkbox" className="mt-0.5" />
                I've read and agree to the Support Agent Acceptable Use statement above.
              </label>

              {state.status === "error" && <p className="text-sm text-rose-600" role="alert">{state.message}</p>}
              <button type="submit" disabled={!token || !agree || state.status === "loading"}
                      data-testid="support-invite-submit" className="w-full btn-brand py-2.5 text-sm font-medium disabled:opacity-50">
                {state.status === "loading" ? "Activating…" : "Activate account"}
              </button>
            </form>
          </>
        )}
      </div>
    </main>
  );
}
